import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, timer, of } from 'rxjs';
import { map, catchError, switchMap, tap, shareReplay, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface FeatureFlagsState {
  [key: string]: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private readonly apiUrl = `${environment.url}api/feature-flags`;
  
  // Start with null to indicate "not loaded yet"
  private flagsSubject = new BehaviorSubject<FeatureFlagsState | null>(null);
  
  // Public observable that components can subscribe to
  public flags$ = this.flagsSubject.asObservable();
  
  // Use a hardcoded interval of 60 seconds (60000ms)
  private readonly POLLING_INTERVAL_MS = 60000;

  constructor(private http: HttpClient) {
    this.startPolling();
  }

  private startPolling(): void {
    // timer(0, POLLING_INTERVAL_MS) starts immediately, then repeats every POLLING_INTERVAL_MS
    timer(0, this.POLLING_INTERVAL_MS).pipe(
      switchMap(() => this.fetchFlags()),
    ).subscribe((flags) => {
      this.flagsSubject.next(flags);
    });
  }

  private fetchFlags(): Observable<FeatureFlagsState> {
    return this.http.get<FeatureFlagsState>(this.apiUrl).pipe(
      catchError((error) => {
        console.error('[FeatureFlagService] Failed to load feature flags:', error);
        // Fallback or keep old state on error. For now returning empty obj.
        return of({});
      })
    );
  }

  /**
   * Check if a specific feature flag is currently enabled.
   */
  public isEnabled(key: string): boolean {
    const currentState = this.flagsSubject.getValue();
    if (!currentState) {
      return false; // Safest fallback when not loaded
    }
    return !!currentState[key];
  }

  /**
   * Helper observable that emits only when the flag's state changes.
   * Useful for route guards or complex component logic.
   */
  public watchFlag(key: string): Observable<boolean> {
    return this.flags$.pipe(
      filter(state => state !== null),
      map(state => !!state![key]),
      shareReplay(1)
    );
  }
}
